import React, { useState } from "react";
import { changePassword, getCurrentUser, logout } from "../api/client";
import { User } from "../types";

interface Props {
  /** Called after a successful change with the refreshed user (must_change cleared). */
  onChanged: (user: User) => void;
  /** Called when the user signs out instead of changing. */
  onLogout: () => void;
  /** Forced mode (issued a temp password) shows the explanatory banner. */
  forced?: boolean;
}

/**
 * Full-screen password-change gate. Shown when a user must set a new password
 * after an admin/CLI reset (forced), and reusable as a voluntary change form.
 */
export default function ChangePassword({ onChanged, onLogout, forced }: Props) {
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow]       = useState(false);   // reveal what was typed in all three boxes

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next.length < 8)  { setError("New password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      const r = await getCurrentUser();
      onChanged(r.data);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? "Could not change password.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try { await logout(); } catch { /* ignore */ }
    onLogout();
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h1 style={s.title}>Set a new password</h1>
        {forced ? (
          <p style={s.banner}>
            Your password was reset to a temporary one. Choose a new password to continue —
            the temporary password stops working once you do.
          </p>
        ) : (
          <p style={s.sub}>Enter your current password and choose a new one.</p>
        )}
        <form onSubmit={submit}>
          <label style={s.label} htmlFor="current">
            {forced ? "Temporary password" : "Current password"}
          </label>
          <input id="current" style={s.input} type={show ? "text" : "password"} value={current}
            autoComplete="current-password"
            onChange={e => setCurrent(e.target.value)} autoFocus required />
          <label style={s.label} htmlFor="next">New password</label>
          <input id="next" style={s.input} type={show ? "text" : "password"} value={next}
            autoComplete="new-password"
            onChange={e => setNext(e.target.value)} minLength={8} required />
          <label style={s.label} htmlFor="confirm">Confirm new password</label>
          <input id="confirm" style={s.input} type={show ? "text" : "password"} value={confirm}
            autoComplete="new-password"
            onChange={e => setConfirm(e.target.value)} minLength={8} required />
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#4a5568",
                          margin:"-6px 0 14px", cursor:"pointer" }}>
            <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
            Show passwords
          </label>
          {error && <p style={s.error}>{error}</p>}
          <button style={s.btn} type="submit" disabled={loading}>
            {loading ? "Saving…" : "Change password"}
          </button>
        </form>
        <button style={s.link} type="button" onClick={signOut}>Sign out</button>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:  { display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#ebf8ff" },
  card:  { background:"#fff", borderRadius:10, padding:"40px 48px", width:400, boxShadow:"0 4px 24px rgba(0,0,0,0.10)" },
  title: { color:"#1a365d", fontSize:24, fontWeight:700, marginBottom:8 },
  sub:   { color:"#4a5568", fontSize:14, marginBottom:24 },
  banner:{ color:"#975a16", background:"#fffaf0", border:"1px solid #f6e05e",
           borderRadius:6, padding:"10px 12px", fontSize:13, marginBottom:20 },
  label: { display:"block", fontSize:13, fontWeight:600, color:"#2d3748", marginBottom:4 },
  input: { display:"block", width:"100%", padding:"8px 10px", marginBottom:16,
           border:"1px solid #cbd5e0", borderRadius:5, fontSize:14 },
  btn:   { width:"100%", padding:"10px 0", background:"#2b6cb0", color:"#fff",
           border:"none", borderRadius:5, fontSize:15, fontWeight:600, cursor:"pointer" },
  link:  { display:"block", margin:"14px auto 0", background:"none", border:"none",
           color:"#4a5568", fontSize:13, cursor:"pointer", textDecoration:"underline" },
  error: { color:"#c53030", fontSize:13, marginBottom:10 },
};
